import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';

interface CanvasSize {
  name: string;
  width: number;
  height: number;
  category: string;
}

const PRESET_SIZES: CanvasSize[] = [
  // Default/Portrait
  { name: 'Portrait (Default)', width: 1080, height: 1350, category: 'Default' },
  
  // Instagram
  { name: 'Instagram Post', width: 1080, height: 1080, category: 'Instagram' },
  { name: 'Instagram Story', width: 1080, height: 1920, category: 'Instagram' },
  { name: 'Instagram Reel', width: 1080, height: 1920, category: 'Instagram' },
  
  // Facebook
  { name: 'Facebook Post', width: 1200, height: 630, category: 'Facebook' },
  { name: 'Facebook Cover', width: 820, height: 312, category: 'Facebook' },
  { name: 'Facebook Story', width: 1080, height: 1920, category: 'Facebook' },
  
  // Twitter/X
  { name: 'Twitter/X Post', width: 1200, height: 675, category: 'Twitter/X' },
  { name: 'Twitter/X Header', width: 1500, height: 500, category: 'Twitter/X' },
  
  // YouTube
  { name: 'YouTube Thumbnail', width: 1280, height: 720, category: 'YouTube' },
  { name: 'YouTube Banner', width: 2560, height: 1440, category: 'YouTube' },
  
  // LinkedIn
  { name: 'LinkedIn Post', width: 1200, height: 627, category: 'LinkedIn' },
  { name: 'LinkedIn Cover', width: 1584, height: 396, category: 'LinkedIn' },
  
  // Custom
  { name: 'Custom Size', width: 800, height: 600, category: 'Custom' },
];

const CreateProject = () => {
  const [projectName, setProjectName] = useState('');
  const [selectedSize, setSelectedSize] = useState<CanvasSize>(PRESET_SIZES[0]);
  const [customWidth, setCustomWidth] = useState(1080);
  const [customHeight, setCustomHeight] = useState(1350);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();

  const handleCreate = async () => {
    if (!projectName.trim()) {
      setError(t('pages.createProject.errors.nameRequired'));
      return;
    }

    // Validate English name
    if (!/^[a-zA-Z0-9_-]+$/.test(projectName)) {
      setError(t('pages.createProject.errors.nameEnglishOnly'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check credits using new FIFO system
      const { data: creditsValue, error: creditsError } = await supabase
        .rpc('get_user_available_credits', { p_user_id: user?.id });

      console.log('🔍 Credits check:', { 
        creditsValue, 
        creditsError, 
        userId: user?.id,
        rawData: creditsValue,
        type: typeof creditsValue
      });

      const remaining = Number(creditsValue) || 0;
      
      if (remaining < 25) {
        setError(t('pages.createProject.errors.insufficientCredits', { credits: remaining }));
        setLoading(false);
        return;
      }

      const width = selectedSize.name === 'Custom Size' ? customWidth : selectedSize.width;
      const height = selectedSize.name === 'Custom Size' ? customHeight : selectedSize.height;

      const { data, error } = await supabase
        .from('projects')
        .insert({
          user_id: user?.id,
          name: projectName,
          description: `${selectedSize.name} (${width}x${height})`,
          editor_data: {
            elements: [],
            canvasWidth: width,
            canvasHeight: height,
            canvasOrientation: width > height ? 'landscape' : 'portrait',
          },
        })
        .select()
        .single();

      if (error) throw error;

      // Deduct 25 credits using FIFO system (with rollback on failure)
      try {
        const { data: creditResult, error: creditError } = await supabase.rpc('use_credits_fifo', {
          p_user_id: user?.id,
          p_amount: 25,
          p_reason: `${t('pages.createProject.createReason')}: ${projectName}`
        });

        if (creditError || (creditResult && creditResult.success === false)) {
          // Rollback created project if deduction failed
          await supabase
            .from('projects')
            .delete()
            .eq('id', data.id);

          const message = (creditError && creditError.message) || creditResult?.error || t('pages.createProject.errors.insufficientCreditsShort');
          setError(`${t('pages.createProject.errors.deductFailed')}: ${message}`);
          setLoading(false);
          return;
        }
      } catch (deductErr: any) {
        // Rollback on unexpected error as well
        await supabase
          .from('projects')
          .delete()
          .eq('id', data.id);
        setError(`${t('pages.createProject.errors.deductFailed')}: ${deductErr.message || t('pages.createProject.errors.unexpectedError')}`);
        setLoading(false);
        return;
      }

      // Navigate to editor with project ID
      navigate(`/editor?project=${data.id}`);
    } catch (err: any) {
      setError(t('pages.createProject.errors.createFailed') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t('pages.createProject.title')}</h1>
          <p className="text-gray-400">{t('pages.createProject.subtitle')}</p>
        </div>

        {/* Card */}
        <div className="bg-zinc-900 rounded-2xl shadow-2xl p-8 border border-zinc-800">
          {/* Cost Notice */}
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
            <div className="text-blue-400 text-sm font-semibold mb-1">{t('pages.createProject.cost.title')}</div>
            <div className="text-blue-200 text-2xl font-bold">{t('pages.createProject.cost.amount')}</div>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Project Name */}
            <div>
              <label htmlFor="projectName" className="block text-sm font-medium text-gray-300 mb-2">
                {t('pages.createProject.name.label')}
              </label>
              <input
                id="projectName"
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all"
                placeholder={t('pages.createProject.name.placeholder')}
                required
                disabled={loading}
                dir="ltr"
              />
              <p className="text-xs text-gray-500 mt-1">{t('pages.createProject.name.example')}</p>
            </div>

            {/* Size Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {t('pages.createProject.size.label')}
              </label>
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-2">
                {PRESET_SIZES.map((size, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSize(size)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      selectedSize.name === size.name
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                    disabled={loading}
                  >
                    <div className="font-semibold text-white text-sm mb-1">{size.name}</div>
                    <div className="text-xs text-gray-400">{size.width} × {size.height}</div>
                    <div className="text-xs text-gray-500 mt-1">{size.category}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Size */}
            {selectedSize.name === 'Custom Size' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">{t('pages.createProject.custom.width')}</label>
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) => setCustomWidth(Math.max(100, parseInt(e.target.value) || 800))}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 rounded-lg focus:border-blue-500 focus:outline-none"
                    min="100"
                    max="5000"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-2">{t('pages.createProject.custom.height')}</label>
                  <input
                    type="number"
                    value={customHeight}
                    onChange={(e) => setCustomHeight(Math.max(100, parseInt(e.target.value) || 600))}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 rounded-lg focus:border-blue-500 focus:outline-none"
                    min="100"
                    max="5000"
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => navigate('/dashboard')}
                disabled={loading}
                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 ml-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('pages.createProject.creating')}
                  </>
                ) : (
                  t('pages.createProject.createButton')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProject;

